/**
 * Documentation action buttons component
 */

import {useState} from 'react';

interface DocsActionButtonsProps {
  pageTitle?: string;
  pagePath?: string;
}

export function DocsActionButtons({pageTitle}: DocsActionButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const handleCopy = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: pageTitle || 'VoxaPilot Documentation',
        url: window.location.href,
      });
    } else {
      handleCopy();
    }
  };

  const handleFeedback = () => {
    // In production, this would open a feedback form or modal
    setFeedbackSent(true);
    setTimeout(() => setFeedbackSent(false), 2000);
  };

  return (
    <div className="flex items-center space-x-2 mt-8 pt-8 border-t border-gray-200">
      <button
        onClick={handleCopy}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-textSecondary hover:text-textPrimary transition-colors"
      >
        {copied ? (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Copied!</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy page</span>
          </>
        )}
      </button>

      <button
        onClick={handleShare}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-textSecondary hover:text-textPrimary transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
        <span>Share feedback</span>
      </button>

      <button
        onClick={handleFeedback}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-textSecondary hover:text-textPrimary transition-colors"
      >
        {feedbackSent ? (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Sent!</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
            <span>Explain more</span>
          </>
        )}
      </button>
    </div>
  );
}

